/**
 * CSV/PDF Export Utility — pure-JS CSV generation, PDF generation, and file download.
 *
 * CSV: No dependencies required — builds a CSV Blob and triggers the browser's
 * native download mechanism via a temporary <a> element.
 *
 * PDF: Uses jsPDF + jspdf-autotable for report PDF generation.
 */

import { iAmHigherAuthority, convertIsoUTCToLocal } from '../libs.jsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ---------------------------------------------------------------------------
// Core CSV builder
// ---------------------------------------------------------------------------

/**
 * Convert an array of objects into a CSV string using the given column headers.
 *
 * @param {Array<{label: string, key: string}>} headers — column definitions
 * @param {Array<object>} rows — data rows
 * @returns {string} — CSV-formatted text (UTF-8 with BOM for Excel compat)
 */
function buildCsv(headers, rows) {
  const escape = (val) => {
    const str = val == null ? '' : String(val);
    // Wrap in quotes if the value contains a comma, quote, or newline
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const headerLine = headers.map((h) => escape(h.label)).join(',');
  const dataLines = rows.map((row) =>
    headers.map((h) => escape(row[h.key])).join(',')
  );

  return [headerLine, ...dataLines].join('\r\n');
}

/**
 * Trigger a browser file download for the given CSV content.
 *
 * @param {string} csvString — raw CSV text
 * @param {string} filename — download filename (e.g. "residents.csv")
 */
function downloadCsv(csvString, filename) {
  // UTF-8 BOM so Excel opens with correct encoding
  const bom = '\uFEFF';
  const blob = new Blob([bom + csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();

  // Cleanup
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
}

// ---------------------------------------------------------------------------
// Invoices Export (Billing)
// ---------------------------------------------------------------------------

const INVOICE_HEADERS = [
  { label: 'Invoice #', key: 'invoice#' },
  { label: 'Status', key: 'status' },
  { label: 'Invoice Date', key: 'invoice_date' },
  { label: 'Due Date', key: 'due_date' },
  { label: 'Total', key: 'total' },
  { label: 'Balance', key: 'balance' },
];

/**
 * Export invoices to CSV and trigger download.
 *
 * @param {Array<object>} invoices — invoice data
 * @param {string} [filename='invoices.csv'] — download filename
 */
export function exportInvoicesCsv(invoices, filename = 'invoices.csv') {
  const csv = buildCsv(INVOICE_HEADERS, invoices);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Wellness Report Export (dynamic columns)
// ---------------------------------------------------------------------------

/**
 * Export wellness report data to CSV using dynamic column definitions.
 *
 * @param {Array<object>} rows — report data (sorted)
 * @param {Array<{key: string, label: string}>} columnDefs — active column definitions
 * @param {string} [filename='wellness-report.csv'] — download filename
 */
export function exportWellnessCsv(rows, columnDefs, filename = 'wellness-report.csv') {
  const headers = columnDefs.map((col) => ({ label: col.label, key: col.key }));
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

/**
 * Export wellness report data to PDF using dynamic column definitions.
 *
 * @param {Array<object>} rows — report data (sorted)
 * @param {Array<{key: string, label: string}>} columnDefs — active column definitions
 * @param {string} title — report title
 * @param {string} dateRange — date range display string
 * @param {string} [filename='wellness-report.pdf'] — download filename
 */
export function exportWellnessPdf(rows, columnDefs, title = 'Wellness Report', dateRange = '', filename = 'wellness-report.pdf') {
  const isWide = columnDefs.length > 5;
  const doc = new jsPDF({ orientation: isWide ? 'landscape' : 'portrait', unit: 'mm', format: 'a4' });

  const pdfHeaders = columnDefs.map((col) => col.label);
  const pdfRows = rows.map((row) =>
    columnDefs.map((col) => {
      const val = row[col.key];
      if (val == null) return '';
      if (typeof val === 'boolean') return val ? 'Yes' : 'No';
      if (val === 1 || val === '1' || val === true) {
        if (col.key === 'checked_in' || col.key === 'rounds_performed') return 'Yes';
      }
      if (val === 0 || val === '0' || val === false) {
        if (col.key === 'checked_in' || col.key === 'rounds_performed') return 'No';
      }
      return String(val);
    })
  );

  doc.setFontSize(14);
  doc.text(title, 14, 15);
  if (dateRange) {
    doc.setFontSize(9);
    doc.text(dateRange, 14, 22);
  }

  autoTable(doc, {
    head: [pdfHeaders],
    body: pdfRows,
    startY: dateRange ? 28 : 22,
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [0, 129, 199], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    margin: { left: 10, right: 10 },
  });

  doc.save(filename);
}

// ---------------------------------------------------------------------------
// Residents Export
// ---------------------------------------------------------------------------

/** CSV header map for resident export — keyed by table column key */
const RESIDENT_HEADERS_MAP = {
  resident_first_name: { label: 'First Name', key: 'resident_first_name' },
  resident_last_name: { label: 'Last Name', key: 'resident_last_name' },
  resident_nickname: { label: 'Nickname', key: 'resident_nickname' },
  area_name: { label: 'Area', key: 'area_name' },
  area_type_name: { label: 'Area Type', key: 'area_type_name' },
  resident_groups: { label: 'Groups', key: '_groups' },
  resident_phone: { label: 'Phone', key: 'resident_phone' },
  resident_email: { label: 'Email', key: 'resident_email' },
  ehr_level_of_care: { label: 'Care Level', key: 'ehr_level_of_care' },
  devices: { label: 'Devices', key: '_devices' },
  check_in_time: { label: 'Last Check-in', key: 'check_in_time' },
  status: { label: 'Status', key: '_status' },
};

/** Column order for resident CSV output */
const RESIDENT_COLUMN_ORDER = [
  'resident_first_name', 'resident_last_name', 'resident_nickname',
  'area_name', 'area_type_name', 'resident_groups', 'resident_phone',
  'resident_email', 'ehr_level_of_care', 'devices', 'check_in_time', 'status',
];

/**
 * Transform a resident row for CSV export (flatten nested arrays to strings).
 *
 * @param {object} r — raw resident object from API
 * @returns {object} — flat object ready for CSV
 */
function transformResident(r) {
  return {
    ...r,
    _groups: (r.resident_groups || []).map((g) => g.resident_group_name).join(', ') || '',
    _devices: (r.devices || []).map((d) => d.device_name || d.device_identifier || '').join(' | ') || '',
    _status: r.is_active ? 'Active' : 'Moved Out',
  };
}

/**
 * Export residents to CSV and trigger download.
 * Only includes columns that are currently visible on the table.
 *
 * @param {Array<object>} residents — resident data (filtered or selected subset)
 * @param {string} [filename='residents.csv'] — download filename
 * @param {Array<string>} [visibleColumns] — currently visible column keys
 */
export function exportResidentsCsv(residents, filename = 'residents.csv', visibleColumns) {
  const headers = RESIDENT_COLUMN_ORDER
    .filter((colKey) => !visibleColumns || visibleColumns.includes(colKey))
    .map((colKey) => RESIDENT_HEADERS_MAP[colKey]);
  const rows = residents.map(transformResident);
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Groups Export
// ---------------------------------------------------------------------------

/** CSV column headers for group export (matches V1 layout) */
const GROUP_HEADERS = [
  { label: 'Group', key: 'resident_group_name' },
  { label: 'Category', key: 'resident_group_category_name' },
  { label: 'Description', key: 'resident_description' },
  { label: 'Residents', key: '_residents' },
];

/**
 * Transform a group row for CSV export.
 *
 * @param {object} g — enriched group object (has `members` array)
 * @returns {object} — flat object ready for CSV
 */
function transformGroup(g) {
  return {
    ...g,
    _residents: (g.members || []).map((m) => m.resident_name).join(', ') || '',
  };
}

/**
 * Export groups to CSV and trigger download.
 *
 * @param {Array<object>} groups — group data (filtered or selected subset)
 * @param {string} [filename='resident-groups.csv'] — download filename
 */
export function exportGroupsCsv(groups, filename = 'resident-groups.csv') {
  const rows = groups.map(transformGroup);
  const csv = buildCsv(GROUP_HEADERS, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Areas Export
// ---------------------------------------------------------------------------

/** CSV header map for area export — keyed by table column key */
const AREA_HEADERS_MAP = {
  area_name: { label: 'Area Name', key: 'area_name' },
  area_type_description: { label: 'Area Type', key: 'area_type_description' },
  area_group_name: { label: 'Area Groups', key: '_area_groups' },
  building_name: { label: 'Building', key: 'building_name' },
  max_occupancy: { label: 'Max Occupancy', key: 'max_occupancy' },
  residents: { label: 'Residents', key: '_residents' },
  devices: { label: 'Devices', key: '_devices' },
};

/** Column order for area CSV output */
const AREA_COLUMN_ORDER = [
  'area_name', 'area_type_description', 'area_group_name', 'building_name',
  'max_occupancy', 'residents', 'devices',
];

/**
 * Transform an area row for CSV export.
 */
function transformArea(a) {
  return {
    ...a,
    _area_groups: (a.area_groups || []).map((g) => g.area_group_name).join(', ') || '',
    _residents: (a.residents || []).map((r) => r.resident_name || `${r.resident_first_name || ''} ${r.resident_last_name || ''}`.trim()).join(', ') || '',
    _devices: (a.devices || []).map((d) => `${d.device_name || ''} ${d.descriptions || ''}`.trim()).join(' | ') || '',
  };
}

/**
 * Export areas to CSV and trigger download.
 * Only includes columns that are currently visible on the table.
 *
 * @param {Array<object>} areas — area data (filtered or selected subset)
 * @param {string} [filename='areas.csv'] — download filename
 * @param {Array<string>} [visibleColumns] — currently visible column keys
 */
export function exportAreasCsv(areas, filename = 'areas.csv', visibleColumns) {
  const headers = AREA_COLUMN_ORDER
    .filter((colKey) => !visibleColumns || visibleColumns.includes(colKey))
    .map((colKey) => AREA_HEADERS_MAP[colKey]);
  const rows = areas.map(transformArea);
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Area Groups Export
// ---------------------------------------------------------------------------

/** CSV column headers for area group export (matches V1 csvAreaGroupHeaders) */
const AREA_GROUP_HEADERS = [
  { label: 'Group Name', key: 'area_group_name' },
  { label: 'Description', key: 'area_group_description' },
  { label: 'Areas', key: '_areas' },
];

/**
 * Transform an area group row for CSV export.
 */
function transformAreaGroup(g) {
  return {
    ...g,
    _areas: (g.areas || []).map((a) => a.area_name).join(', ') || '',
  };
}

/**
 * Export area groups to CSV and trigger download.
 *
 * @param {Array<object>} areaGroups — area group data
 * @param {string} [filename='area-groups.csv'] — download filename
 */
export function exportAreaGroupsCsv(areaGroups, filename = 'area-groups.csv') {
  const rows = areaGroups.map(transformAreaGroup);
  const csv = buildCsv(AREA_GROUP_HEADERS, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Buildings Export
// ---------------------------------------------------------------------------

/** CSV column headers for building export (matches V1 csvBuildingHeaders) */
const BUILDING_HEADERS = [
  { label: 'Building Name', key: 'building_name' },
  { label: 'Street Address', key: 'street_address' },
  { label: 'City', key: 'city' },
  { label: 'State', key: 'state' },
  { label: 'Zip Code', key: 'zipcode' },
  { label: 'Country', key: 'country' },
  { label: 'Areas', key: '_areas' },
];

/**
 * Transform a building row for CSV export.
 */
function transformBuilding(b) {
  return {
    ...b,
    _areas: (b.areas || []).map((a) => a.area_name).join(', ') || '',
  };
}

/**
 * Export buildings to CSV and trigger download.
 *
 * @param {Array<object>} buildings — building data
 * @param {string} [filename='buildings.csv'] — download filename
 */
export function exportBuildingsCsv(buildings, filename = 'buildings.csv') {
  const rows = buildings.map(transformBuilding);
  const csv = buildCsv(BUILDING_HEADERS, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Users Export
// ---------------------------------------------------------------------------

/** CSV column headers for user export (base columns) */
const USER_HEADERS_BASE = [
  { label: 'Username', key: 'username' },
  { label: 'Display Name', key: 'display_name' },
  { label: 'Email', key: 'email' },
  { label: 'Phone', key: 'phone' },
  { label: 'Title', key: 'title' },
  { label: 'Role', key: 'user_role_name' },
  { label: 'Groups', key: 'group_name' },
];

/** Extra columns visible only to higher-authority users (weight > 99) */
const USER_HEADERS_ADMIN = [
  { label: 'User ID', key: 'user_id' },
  { label: 'Cognito ID', key: 'cognito_id' },
];

/** App Status columns — included when is_logged_in is visible on the table */
const USER_HEADERS_LOGIN = [
  { label: 'App Status', key: 'is_logged_in' },
  { label: 'Last Login Time', key: 'last_login_time' },
];

/**
 * Export users to CSV and trigger download.
 * Includes User ID and Cognito ID columns for admins (role_weight > 99).
 * Includes App Status and Last Login Time when the is_logged_in column is visible.
 *
 * @param {Array<object>} users — user data (filtered or selected subset)
 * @param {string} [filename='users.csv'] — download filename
 * @param {number} [myRoleWeight=10] — current user's role weight
 * @param {Array<string>} [visibleColumns] — currently visible column keys
 */
export function exportUsersCsv(users, filename = 'users.csv', myRoleWeight = 10, visibleColumns) {
  let headers = iAmHigherAuthority(99, myRoleWeight)
    ? [...USER_HEADERS_ADMIN, ...USER_HEADERS_BASE]
    : [...USER_HEADERS_BASE];

  if (visibleColumns && visibleColumns.includes('is_logged_in')) {
    headers = [...headers, ...USER_HEADERS_LOGIN];
  }

  // Map is_logged_in numeric to readable label for CSV output
  const rows = users.map((u) => ({
    ...u,
    is_logged_in: u.is_logged_in ? 'Online' : 'Offline',
  }));

  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// User Groups Export
// ---------------------------------------------------------------------------

/** All possible CSV column headers for user group export (keyed to table column keys) */
const USER_GROUP_HEADERS_MAP = {
  group_name: { label: 'Group Name', key: 'group_name' },
  capcode: { label: 'Pager Channel', key: 'capcode' },
  user_count: { label: 'Users', key: '_users' },
};

/** Column order for CSV output */
const USER_GROUP_COLUMN_ORDER = ['group_name', 'capcode', 'user_count'];

/** Extra columns visible only to higher-authority users */
const USER_GROUP_HEADERS_ADMIN = [
  { label: 'Group ID', key: 'group_id' },
];

/**
 * Transform a user group row for CSV export.
 */
function transformUserGroup(g) {
  return {
    ...g,
    _users: (g.users || []).map((u) => u.display_name || u.username).join(', ') || '',
  };
}

/**
 * Export user groups to CSV and trigger download.
 * Only includes columns that are currently visible on the table.
 *
 * @param {Array<object>} groups — user group data
 * @param {string} [filename='user-groups.csv'] — download filename
 * @param {number} [myRoleWeight=10] — current user's role weight
 * @param {Array<string>} [visibleColumns] — currently visible column keys
 */
export function exportUserGroupsCsv(groups, filename = 'user-groups.csv', myRoleWeight = 10, visibleColumns) {
  const adminHeaders = iAmHigherAuthority(99, myRoleWeight) ? [...USER_GROUP_HEADERS_ADMIN] : [];

  // Build data headers from visible columns in defined order
  const dataHeaders = USER_GROUP_COLUMN_ORDER
    .filter((colKey) => !visibleColumns || visibleColumns.includes(colKey))
    .map((colKey) => USER_GROUP_HEADERS_MAP[colKey]);

  const headers = [...adminHeaders, ...dataHeaders];
  const rows = groups.map(transformUserGroup);
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Devices Export
// ---------------------------------------------------------------------------

const DEVICE_HEADERS_ADMIN = [
  { label: 'Device ID', key: 'device_id' },
];

/** CSV header map for nurse call device export — keyed by table column key */
const DEVICE_HEADERS_MAP = {
  device_name: { label: 'Name', key: 'device_name' },
  descriptions: { label: 'Description', key: 'descriptions' },
  inovonics_model_name: { label: 'Device Model', key: 'inovonics_model_name' },
  manufacture_uuid: { label: 'Serial Number', key: 'manufacture_uuid' },
  residents: { label: 'Resident Name', key: '_resident_names' },
  area_name: { label: 'Area Name', key: 'area_name' },
  low_battery: { label: 'Battery', key: 'low_battery' },
  tamper: { label: 'Tamper', key: 'tamper' },
  offline: { label: 'Offline Status', key: 'offline' },
  last_alarm: { label: 'Last Alarm', key: 'last_alarm' },
  last_active: { label: 'Last Active', key: 'last_active' },
  is_active: { label: 'Active Status', key: 'is_active' },
};

/** Column order for nurse call device CSV output */
const DEVICE_COLUMN_ORDER = [
  'device_name', 'descriptions', 'inovonics_model_name', 'manufacture_uuid',
  'residents', 'area_name', 'low_battery', 'tamper', 'offline',
  'last_alarm', 'last_active', 'is_active',
];

/**
 * Transform a device row for CSV export — flatten residents array to names string.
 */
function transformDevice(d) {
  return {
    ...d,
    _resident_names: (d.residents || []).map((r) => r.resident_name).filter(Boolean).join(', ') || d.resident_name || '',
  };
}

/**
 * Export devices to CSV and trigger download.
 * Only includes columns that are currently visible on the table.
 *
 * @param {Array<object>} devices — device data
 * @param {string} [filename='devices.csv'] — download filename
 * @param {number} [myRoleWeight=10] — current user's role weight
 * @param {Array<string>} [visibleColumns] — currently visible column keys
 */
export function exportDevicesCsv(devices, filename = 'devices.csv', myRoleWeight = 10, visibleColumns) {
  const adminHeaders = iAmHigherAuthority(99, myRoleWeight) ? [...DEVICE_HEADERS_ADMIN] : [];
  const dataHeaders = DEVICE_COLUMN_ORDER
    .filter((colKey) => !visibleColumns || visibleColumns.includes(colKey))
    .map((colKey) => DEVICE_HEADERS_MAP[colKey]);
  const headers = [...adminHeaders, ...dataHeaders];
  const rows = devices.map(transformDevice);
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Wandering Devices Export
// ---------------------------------------------------------------------------

/** CSV header map for wandering device export — keyed by table column key */
const WANDERING_HEADERS_MAP = {
  device_name: { label: 'Name', key: 'device_name' },
  descriptions: { label: 'Description', key: 'descriptions' },
  model_name: { label: 'Model', key: 'model_name' },
  manufacture_uuid: { label: 'Badge Number', key: 'manufacture_uuid' },
  resident_name: { label: 'Resident', key: 'resident_name' },
  area_name: { label: 'Area', key: 'area_name' },
  is_active: { label: 'Active Status', key: '_active_status' },
};

/** Column order for wandering device CSV output */
const WANDERING_COLUMN_ORDER = [
  'device_name', 'descriptions', 'model_name', 'manufacture_uuid',
  'resident_name', 'area_name', 'is_active',
];

/**
 * Transform a wandering device row for CSV export.
 */
function transformWanderingDevice(d) {
  return {
    ...d,
    _active_status: d.is_active ? 'Active' : 'Inactive',
  };
}

/**
 * Export wandering devices to CSV and trigger download.
 * Only includes columns that are currently visible on the table.
 *
 * @param {Array<object>} devices — wandering device data
 * @param {string} [filename='wandering-devices.csv'] — download filename
 * @param {number} [myRoleWeight=10] — current user's role weight
 * @param {Array<string>} [visibleColumns] — currently visible column keys
 */
export function exportWanderingDevicesCsv(devices, filename = 'wandering-devices.csv', myRoleWeight = 10, visibleColumns) {
  const adminHeaders = iAmHigherAuthority(99, myRoleWeight) ? [...DEVICE_HEADERS_ADMIN] : [];
  const dataHeaders = WANDERING_COLUMN_ORDER
    .filter((colKey) => !visibleColumns || visibleColumns.includes(colKey))
    .map((colKey) => WANDERING_HEADERS_MAP[colKey]);
  const headers = [...adminHeaders, ...dataHeaders];
  const rows = devices.map(transformWanderingDevice);
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// System Devices Export
// ---------------------------------------------------------------------------

/** CSV header map for system device export — keyed by table column key */
const SYSTEM_HEADERS_MAP = {
  device_name: { label: 'Name', key: 'device_name' },
  model_name: { label: 'Model', key: 'model_name' },
  manufacture_uuid: { label: 'Serial Number', key: 'manufacture_uuid' },
  area_name: { label: 'Area', key: 'area_name' },
  network_id: { label: 'Network', key: 'network_id' },
  last_active: { label: 'Last Active', key: 'last_active' },
  mac_addr: { label: 'Last Known Server', key: 'mac_addr' },
};

/** Column order for system device CSV output */
const SYSTEM_COLUMN_ORDER = [
  'device_name', 'model_name', 'manufacture_uuid', 'area_name',
  'network_id', 'last_active', 'mac_addr',
];

/**
 * Export system devices to CSV and trigger download.
 * Only includes columns that are currently visible on the table.
 *
 * @param {Array<object>} devices — system device data
 * @param {string} [filename='system-devices.csv'] — download filename
 * @param {number} [myRoleWeight=10] — current user's role weight
 * @param {Array<string>} [visibleColumns] — currently visible column keys
 */
export function exportSystemDevicesCsv(devices, filename = 'system-devices.csv', myRoleWeight = 10, visibleColumns) {
  const adminHeaders = iAmHigherAuthority(99, myRoleWeight) ? [...DEVICE_HEADERS_ADMIN] : [];
  const dataHeaders = SYSTEM_COLUMN_ORDER
    .filter((colKey) => !visibleColumns || visibleColumns.includes(colKey))
    .map((colKey) => SYSTEM_HEADERS_MAP[colKey]);
  const headers = [...adminHeaders, ...dataHeaders];
  const csv = buildCsv(headers, devices);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Speaker Devices Export (SIP / Grandstream)
// ---------------------------------------------------------------------------

/** CSV header map for speaker device export — keyed by table column key */
const SPEAKER_HEADERS_MAP = {
  device_name: { label: 'Name', key: 'device_name' },
  descriptions: { label: 'Description', key: 'descriptions' },
  model_name: { label: 'Model', key: 'model_name' },
  manufacture_uuid: { label: 'Serial Number', key: 'manufacture_uuid' },
  residents: { label: 'Residents', key: '_resident_names' },
  area_name: { label: 'Area', key: 'area_name' },
  sip_extension: { label: 'Extension', key: 'sip_extension' },
  is_active: { label: 'Active Status', key: '_active_status' },
};

/** Column order for speaker device CSV output */
const SPEAKER_COLUMN_ORDER = [
  'device_name', 'descriptions', 'model_name', 'manufacture_uuid',
  'residents', 'area_name', 'sip_extension', 'is_active',
];

/**
 * Transform a speaker device row for CSV export — flatten residents array.
 */
function transformSpeakerDevice(d) {
  return {
    ...d,
    _resident_names: (d.residents || []).map((r) => r.resident_name).filter(Boolean).join(', '),
    _active_status: d.is_active ? 'Active' : 'Inactive',
  };
}

/**
 * Export speaker devices to CSV and trigger download.
 * Only includes columns that are currently visible on the table.
 *
 * @param {Array<object>} devices — speaker device data
 * @param {string} [filename='speaker-devices.csv'] — download filename
 * @param {number} [myRoleWeight=10] — current user's role weight
 * @param {Array<string>} [visibleColumns] — currently visible column keys
 */
export function exportSpeakerDevicesCsv(devices, filename = 'speaker-devices.csv', myRoleWeight = 10, visibleColumns) {
  const adminHeaders = iAmHigherAuthority(99, myRoleWeight) ? [...DEVICE_HEADERS_ADMIN] : [];
  const dataHeaders = SPEAKER_COLUMN_ORDER
    .filter((colKey) => !visibleColumns || visibleColumns.includes(colKey))
    .map((colKey) => SPEAKER_HEADERS_MAP[colKey]);
  const headers = [...adminHeaders, ...dataHeaders];
  const rows = devices.map(transformSpeakerDevice);
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Servers Export
// ---------------------------------------------------------------------------

const SERVER_HEADERS_ADMIN = [
  { label: 'Device ID', key: 'palcare_device_id' },
];

/** CSV header map for server export — keyed by table column key */
const SERVER_HEADERS_MAP = {
  description: { label: 'Description', key: 'description' },
  palcare_interface_name: { label: 'Service', key: 'palcare_interface_name' },
  area_name: { label: 'Area', key: 'area_name' },
  ip_address: { label: 'IP Address', key: 'ip_address' },
  mac_addr: { label: 'Mac Address', key: 'mac_addr' },
  is_active: { label: 'Status', key: '_status' },
  last_heard: { label: 'Last Update', key: 'last_heard' },
};

/** Column order for server CSV output */
const SERVER_COLUMN_ORDER = [
  'description', 'palcare_interface_name', 'area_name', 'ip_address',
  'mac_addr', 'is_active', 'last_heard',
];

/**
 * Transform a server row for CSV export.
 */
function transformServer(s) {
  return {
    ...s,
    _status: s.is_active ? 'Active' : 'Backup',
  };
}

/**
 * Export servers to CSV and trigger download.
 * Only includes columns that are currently visible on the table.
 *
 * @param {Array<object>} servers — server data
 * @param {string} [filename='servers.csv'] — download filename
 * @param {number} [myRoleWeight=10] — current user's role weight
 * @param {Array<string>} [visibleColumns] — currently visible column keys
 */
export function exportServersCsv(servers, filename = 'servers.csv', myRoleWeight = 10, visibleColumns) {
  const adminHeaders = iAmHigherAuthority(99, myRoleWeight) ? [...SERVER_HEADERS_ADMIN] : [];
  const dataHeaders = SERVER_COLUMN_ORDER
    .filter((colKey) => !visibleColumns || visibleColumns.includes(colKey))
    .map((colKey) => SERVER_HEADERS_MAP[colKey]);
  const headers = [...adminHeaders, ...dataHeaders];
  const rows = servers.map(transformServer);
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Reason Codes Export
// ---------------------------------------------------------------------------

/** CSV column headers for reason code export */
const REASON_CODE_HEADERS = [
  { label: 'Reason', key: 'reason_code_description' },
  { label: 'Reason Category', key: 'pal_reason_description' },
];

/**
 * Export reason codes to CSV and trigger download.
 *
 * @param {Array<object>} reasons — reason code data (must have pal_reason_description resolved)
 * @param {string} [filename='reason-codes.csv'] — download filename
 */
export function exportReasonCodesCsv(reasons, filename = 'reason-codes.csv') {
  const csv = buildCsv(REASON_CODE_HEADERS, reasons);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Preform Notes Export
// ---------------------------------------------------------------------------

/** CSV column headers for preform note template export */
const PREFORM_NOTE_HEADERS = [
  { label: 'Note Template', key: 'preform_note' },
  { label: 'Initially Added', key: '_create_time' },
  { label: 'Last Updated', key: '_last_modified' },
  { label: 'Last Updated By', key: 'display_name' },
];

/**
 * Export preform note templates to CSV and trigger download.
 * Expects rows with pre-formatted date fields (_create_time, _last_modified).
 *
 * @param {Array<object>} notes — preform note data
 * @param {string} [filename='note-templates.csv'] — download filename
 */
export function exportPreformNotesCsv(notes, filename = 'note-templates.csv') {
  const csv = buildCsv(PREFORM_NOTE_HEADERS, notes);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Audit Logs Export
// ---------------------------------------------------------------------------

/** CSV column headers for audit log export */
const AUDIT_LOG_HEADERS = [
  { label: 'Timestamp', key: '_create_time' },
  { label: 'Action', key: 'action' },
  { label: 'From', key: 'prev_value' },
  { label: 'To', key: 'new_value' },
  { label: 'Source', key: 'source' },
  { label: 'User', key: 'display_name' },
];

/**
 * Export audit logs to CSV and trigger download.
 * Expects rows with pre-formatted _create_time field.
 *
 * @param {Array<object>} logs — audit log data
 * @param {string} [filename='audit-logs.csv'] — download filename
 */
export function exportAuditLogsCsv(logs, filename = 'audit-logs.csv') {
  const csv = buildCsv(AUDIT_LOG_HEADERS, logs);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Staging Areas Export (Master File Upload Preview)
// ---------------------------------------------------------------------------

/** CSV column headers for staging area export */
const STAGING_AREA_HEADERS = [
  { label: 'Name', key: 'area_name' },
  { label: 'Area Type', key: 'area_type_description' },
  { label: 'Residents', key: 'resident_name' },
  { label: 'Devices', key: 'device_name' },
];

/**
 * Export staging areas to CSV and trigger download.
 *
 * @param {Array<object>} areas — staging area data
 * @param {string} [filename='staging-areas.csv'] — download filename
 */
export function exportStagingAreasCsv(areas, filename = 'staging-areas.csv') {
  const csv = buildCsv(STAGING_AREA_HEADERS, areas);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Staging Residents Export (Master File Upload Preview)
// ---------------------------------------------------------------------------

/** CSV column headers for staging resident export */
const STAGING_RESIDENT_HEADERS = [
  { label: 'First Name', key: 'resident_first_name' },
  { label: 'Last Name', key: 'resident_last_name' },
  { label: 'Area', key: 'area_name' },
  { label: 'Phone #', key: 'resident_phone' },
  { label: 'Email', key: 'resident_email' },
];

/**
 * Export staging residents to CSV and trigger download.
 *
 * @param {Array<object>} residents — staging resident data
 * @param {string} [filename='staging-residents.csv'] — download filename
 */
export function exportStagingResidentsCsv(residents, filename = 'staging-residents.csv') {
  const csv = buildCsv(STAGING_RESIDENT_HEADERS, residents);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Staging Devices Export (Master File Upload Preview)
// ---------------------------------------------------------------------------

/** CSV column headers for staging device export */
const STAGING_DEVICE_HEADERS = [
  { label: 'Name', key: 'device_name' },
  { label: 'Description', key: 'descriptions' },
  { label: 'Model', key: 'model_name' },
  { label: 'Serial Number', key: 'manufacture_uuid' },
  { label: 'Resident', key: 'resident_name' },
  { label: 'Area', key: 'area_name' },
];

/**
 * Export staging devices to CSV and trigger download.
 *
 * @param {Array<object>} devices — staging device data
 * @param {string} [filename='staging-devices.csv'] — download filename
 */
export function exportStagingDevicesCsv(devices, filename = 'staging-devices.csv') {
  const csv = buildCsv(STAGING_DEVICE_HEADERS, devices);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// All Servers Export (cross-campus)
// ---------------------------------------------------------------------------

const ALL_SERVERS_HEADERS = [
  { label: 'Campus ID', key: 'campus_id' },
  { label: 'Campus Name', key: 'campus_name' },
  { label: 'Corporation ID', key: 'corporate_id' },
  { label: 'Corporation Name', key: 'corporate_name' },
  { label: 'Code Version', key: 'version_code' },
  { label: 'Checkin Time', key: 'checkin_time' },
  { label: 'Recent Alarm Time', key: 'recent_alarm_input_time' },
  { label: 'DB Version', key: 'version_database' },
];

/**
 * Export all servers (cross-campus) to CSV and trigger download.
 *
 * @param {Array<object>} servers — all-servers data
 * @param {string} [filename='all_servers.csv'] — download filename
 */
export function exportAllServersCsv(servers, filename = 'all_servers.csv') {
  const csv = buildCsv(ALL_SERVERS_HEADERS, servers);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Campuses Export (cross-campus)
// ---------------------------------------------------------------------------

const CAMPUS_HEADERS = [
  { label: 'Campus ID', key: 'campus_id' },
  { label: 'Campus 2.0 ID', key: 'campus_guid' },
  { label: 'Campus Name', key: 'campus_name' },
  { label: 'Corporation Name', key: 'corporate_name' },
  { label: 'Address', key: 'campus_street_address' },
  { label: 'City', key: 'campus_city' },
  { label: 'State', key: 'campus_state' },
  { label: 'Zip Code', key: 'campus_zip' },
  { label: 'Email', key: 'campus_email' },
  { label: 'Phone #', key: 'campus_phone' },
  { label: 'Has Checkin', key: '_has_checkin' },
  { label: 'Has Rounds', key: '_has_rounds' },
  { label: 'Has Light Ctrl', key: '_has_lightctrl' },
  { label: 'Has Offsite', key: '_has_offsite' },
  { label: 'Service Plan', key: '_service_plan' },
];

/**
 * Transform a campus row for CSV export — convert booleans to readable labels.
 */
function transformCampus(c) {
  return {
    ...c,
    _has_checkin: c.use_checkin ? 'Yes' : 'No',
    _has_rounds: c.use_rounds ? 'Yes' : 'No',
    _has_lightctrl: c.use_lightctrl ? 'Yes' : 'No',
    _has_offsite: c.use_offsite ? 'Yes' : 'No',
    _service_plan: c.service_plan_active ? 'Plan Active' : 'No Plan',
  };
}

/**
 * Export campuses (cross-campus) to CSV and trigger download.
 *
 * @param {Array<object>} campuses — campus data
 * @param {string} [filename='campuses.csv'] — download filename
 */
export function exportCampusesCsv(campuses, filename = 'campuses.csv') {
  const rows = campuses.map(transformCampus);
  const csv = buildCsv(CAMPUS_HEADERS, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Corporations Export (cross-corporation)
// ---------------------------------------------------------------------------

const CORPORATION_HEADERS = [
  { label: 'Corporate Name', key: 'corporate_name' },
  { label: 'Corporate ID', key: 'corporate_id' },
  { label: 'Corporate 2.0 ID', key: 'corporate_guid' },
];

/**
 * Export corporations to CSV and trigger download.
 *
 * @param {Array<object>} corporations — corporation data
 * @param {string} [filename='corporations.csv'] — download filename
 */
export function exportCorporationsCsv(corporations, filename = 'corporations.csv') {
  const csv = buildCsv(CORPORATION_HEADERS, corporations);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Wellness Settings — Check-in Windows
// ---------------------------------------------------------------------------

const CHECKIN_WINDOW_HEADERS = [
  { label: 'Description', key: 'description' },
  { label: 'Start Time', key: 'window_start_display' },
  { label: 'End Time', key: 'window_end_display' },
  { label: 'Alarm On', key: 'generate_alarm_display' },
  { label: 'Alarm Time', key: 'alarm_at_display' },
  { label: 'Enabled', key: 'enabled_display' },
  { label: 'Resident Groups', key: 'resident_group_count' },
];

export function exportCheckinWindowsCsv(rows, filename = 'checkin-windows.csv') {
  const csv = buildCsv(CHECKIN_WINDOW_HEADERS, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Wellness Settings — Rounds Windows
// ---------------------------------------------------------------------------

const ROUNDS_WINDOW_HEADERS = [
  { label: 'Description', key: 'description' },
  { label: 'Start Time', key: 'window_start_display' },
  { label: 'End Time', key: 'window_end_display' },
  { label: '# Checks', key: 'interval' },
  { label: 'Alarm On', key: 'generate_alarm_display' },
  { label: 'Alarm Time', key: 'alarm_at_display' },
  { label: 'Enabled', key: 'enabled_display' },
  { label: 'Area Groups', key: 'area_group_count' },
];

export function exportRoundsWindowsCsv(rows, filename = 'rounds-windows.csv') {
  const csv = buildCsv(ROUNDS_WINDOW_HEADERS, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Wellness Settings — Careflow
// ---------------------------------------------------------------------------

const CAREFLOW_HEADERS = [
  { label: 'Description', key: 'careflow_description' },
  { label: 'Category', key: 'event_category_display' },
  { label: 'Status', key: 'status_display' },
  { label: 'Active Time', key: 'time_display' },
  { label: 'Mobile', key: 'mobile_display' },
  { label: 'Text', key: 'text_display' },
  { label: 'Voice', key: 'voice_display' },
  { label: 'Email', key: 'email_display' },
  { label: 'Pager', key: 'pager_display' },
  { label: 'Offsite', key: 'offsite_display' },
  { label: 'Mute', key: 'mute_display' },
  { label: 'Reporting', key: 'reporting_display' },
  { label: 'References', key: 'references_count' },
];

/** Map of careflow output column keys → campusSettings flag keys. Mirrors
 *  OUTPUT_COLUMN_FLAG_KEYS in CareflowTable.jsx. */
const CAREFLOW_OUTPUT_FLAG_KEYS = {
  mobile_display: 'app_active',
  text_display: 'text_messaging_active',
  voice_display: 'voice_active',
  email_display: 'email_active',
  pager_display: 'pager_active',
  offsite_display: 'offsite_active',
};

/**
 * Export careflows to CSV. When `campusSettings` is provided, output columns
 * (Mobile/Text/Voice/Email/Pager/Offsite) whose global toggle is off are
 * dropped from the CSV.
 *
 * @param {Array<object>} rows — careflow rows (already enriched)
 * @param {string} [filename='careflows.csv']
 * @param {object} [campusSettings] — global output settings flags
 */
export function exportCareflowCsv(rows, filename = 'careflows.csv', campusSettings) {
  const headers = campusSettings
    ? CAREFLOW_HEADERS.filter((h) => {
        const flagKey = CAREFLOW_OUTPUT_FLAG_KEYS[h.key];
        return !flagKey || campusSettings[flagKey] === 1;
      })
    : CAREFLOW_HEADERS;
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Environmental Settings — Temperature / Humidity / Battery
// ---------------------------------------------------------------------------

const ENV_TEMP_HUMIDITY_HEADERS = [
  { label: 'Description', key: 'description' },
  { label: 'Type', key: 'type_display' },
  { label: 'Low Threshold', key: 'low_alarm' },
  { label: 'High Threshold', key: 'high_alarm' },
  { label: 'Alarm On', key: 'alarm_display' },
  { label: 'Report Interval (min)', key: 'interval_display' },
  { label: 'Device Count', key: 'device_count' },
];

const ENV_BATTERY_HEADERS = [
  { label: 'Description', key: 'description' },
  { label: 'Type', key: 'type_display' },
  { label: 'Low Battery (V)', key: 'low_alarm' },
  { label: 'Alarm On', key: 'alarm_display' },
  { label: 'Report Interval (min)', key: 'interval_display' },
  { label: 'Device Count', key: 'device_count' },
];

// ---------------------------------------------------------------------------
// Light Controller Settings — Channels
// ---------------------------------------------------------------------------

const LIGHT_CHANNEL_HEADERS = [
  { label: 'Channel #', key: 'channel_id' },
  { label: 'Name', key: 'channel_name' },
  { label: 'Color', key: 'color_label' },
  { label: 'Flashing', key: 'flashing_display' },
  { label: 'Devices', key: 'device_count' },
];

/**
 * Export light controller channels to CSV and trigger download.
 *
 * @param {Array<object>} rows — enriched channel data (with color_label, flashing_display, device_count)
 * @param {string} [controllerName='channels'] — controller name for default filename
 * @param {string} [filename] — override download filename
 */
export function exportLightChannelsCsv(rows, controllerName = 'channels', filename) {
  const defaultFilename = `${controllerName.replace(/\s+/g, '-').toLowerCase()}-channels.csv`;
  const csv = buildCsv(LIGHT_CHANNEL_HEADERS, rows);
  downloadCsv(csv, filename || defaultFilename);
}

export function exportEnvironmentalCsv(rows, envType, filename) {
  const isBattery = envType === 'battery';
  const headers = isBattery ? ENV_BATTERY_HEADERS : ENV_TEMP_HUMIDITY_HEADERS;
  const defaultFilename = `${envType || 'environmental'}-settings.csv`;
  const csv = buildCsv(headers, rows);
  downloadCsv(csv, filename || defaultFilename);
}

// ---------------------------------------------------------------------------
// PalPendant Display Profiles Export
// ---------------------------------------------------------------------------

const PENDANT_PROFILE_HEADERS = [
  { label: 'Profile Name', key: 'profile_name' },
];

/**
 * Export pendant display profiles to CSV and trigger download.
 *
 * @param {Array<object>} rows — profile data
 * @param {string} [filename='pendant-profiles.csv'] — download filename
 */
export function exportPendantProfilesCsv(rows, filename = 'pendant-profiles.csv') {
  const csv = buildCsv(PENDANT_PROFILE_HEADERS, rows);
  downloadCsv(csv, filename);
}

// ---------------------------------------------------------------------------
// Alert Details Report Export
// ---------------------------------------------------------------------------

/** Base CSV column headers for alert details export (matches V1 layout) */
const ALERT_DETAIL_HEADERS = [
  { label: 'Event Start', key: 'event_datetime_local' },
  { label: 'Event Category', key: 'event_category_name' },
  { label: 'Event Type', key: 'event_type_name' },
  { label: 'Resident', key: 'resident_name' },
  { label: 'Area', key: 'area_name' },
  { label: 'Device Name', key: 'description' },
  { label: 'Device Number', key: 'manufacture_uuid' },
  { label: 'Accepted By', key: 'display_name' },
  { label: 'Event Accepted', key: 'accept_datetime_local' },
  { label: 'Event Cleared', key: 'ack_datetime_local' },
  { label: 'Event Finished', key: 'finish_datetime_local' },
  { label: 'Response (min)', key: 'response_time' },
  { label: 'Service Time (min)', key: 'service_time' },
  { label: 'Reason', key: 'reason_code_description' },
];

/**
 * Map table column keys → CSV header keys for Alert Details.
 * Some table columns combine multiple data fields (e.g. "Category/Type" → two CSV columns).
 */
const ALERT_TABLE_TO_CSV_MAP = {
  event_datetime_local: ['event_datetime_local'],
  event_type_name: ['event_category_name', 'event_type_name'],
  resident_name: ['resident_name'],
  area_name: ['area_name'],
  description: ['description', 'manufacture_uuid'],
  display_name: ['display_name'],
  ack_datetime_local: ['accept_datetime_local', 'ack_datetime_local'],
  finish_datetime_local: ['finish_datetime_local'],
  response_time: ['response_time'],
  service_time: ['service_time'],
  reason_code_description: ['reason_code_description'],
};

/** Filter alert detail headers by visible table columns */
function filterAlertHeaders(headers, visibleColumns) {
  if (!visibleColumns || visibleColumns.length === 0) return headers;
  const allowedKeys = new Set();
  visibleColumns.forEach((tableKey) => {
    const csvKeys = ALERT_TABLE_TO_CSV_MAP[tableKey];
    if (csvKeys) csvKeys.forEach((k) => allowedKeys.add(k));
  });
  return headers.filter((h) => allowedKeys.has(h.key));
}

/**
 * Export alert details to CSV with dynamic note columns.
 *
 * @param {Array<object>} rows — event data with optional `notes` arrays
 * @param {number} maxNotes — maximum note count across all rows
 * @param {string} [filename='alert-details.csv']
 * @param {Array<string>} [visibleColumns] — visible table column keys (filters CSV columns)
 */
export function exportAlertDetailsCsv(rows, maxNotes = 0, filename = 'alert-details.csv', visibleColumns) {
  // Build dynamic note headers
  const noteHeaders = [];
  for (let i = 1; i <= maxNotes; i++) {
    noteHeaders.push({ label: `Note ${i}`, key: `_note_${i}` });
  }
  const filteredBase = filterAlertHeaders(ALERT_DETAIL_HEADERS, visibleColumns);
  const headers = [...filteredBase, ...noteHeaders];

  // Flatten notes into row fields
  const transformed = rows.map((row) => {
    const flat = { ...row };
    const notes = row.notes || [];
    for (let i = 0; i < maxNotes; i++) {
      flat[`_note_${i + 1}`] = notes[i] ? notes[i].note || '' : '';
    }
    return flat;
  });

  const csv = buildCsv(headers, transformed);
  downloadCsv(csv, filename);
}

/**
 * Full PDF column definitions for Alert Details (label + key pairs for filtering).
 */
const ALERT_PDF_COLUMNS = [
  { label: 'Event Start', key: 'event_datetime_local' },
  { label: 'Category', key: 'event_category_name' },
  { label: 'Type', key: 'event_type_name' },
  { label: 'Resident', key: 'resident_name' },
  { label: 'Area', key: 'area_name' },
  { label: 'Device', key: 'description' },
  { label: 'Accepted By', key: 'display_name' },
  { label: 'Cleared', key: 'ack_datetime_local' },
  { label: 'Finished', key: 'finish_datetime_local' },
  { label: 'Response', key: 'response_time' },
  { label: 'Service', key: 'service_time' },
  { label: 'Reason', key: 'reason_code_description' },
];

/**
 * Export alert details to PDF (landscape A4).
 *
 * @param {Array<object>} rows — event data
 * @param {string} title — report title
 * @param {string} dateRange — e.g. "01/15/2026 12:00 AM - 01/16/2026 11:59 PM"
 * @param {string} [filename='alert-details.pdf']
 * @param {Array<string>} [visibleColumns] — visible table column keys (filters PDF columns)
 */
export function exportAlertDetailsPdf(rows, title = 'Alert Details Report', dateRange = '', filename = 'alert-details.pdf', visibleColumns) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  const filteredCols = filterAlertHeaders(ALERT_PDF_COLUMNS, visibleColumns);
  const pdfHeaders = filteredCols.map((col) => col.label);

  const formatValue = (row, key) => {
    if (key === 'service_time') {
      return row[key] != null && row[key] !== 0 ? String(row[key]) : '-';
    }
    if (key === 'response_time') {
      return row[key] != null ? String(row[key]) : '';
    }
    return row[key] != null ? String(row[key]) : '';
  };

  const pdfRows = rows.map((row) =>
    filteredCols.map((col) => formatValue(row, col.key))
  );

  // Title
  doc.setFontSize(14);
  doc.text(title, 14, 15);
  if (dateRange) {
    doc.setFontSize(9);
    doc.text(dateRange, 14, 22);
  }

  autoTable(doc, {
    head: [pdfHeaders],
    body: pdfRows,
    startY: dateRange ? 28 : 22,
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [0, 129, 199], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    margin: { left: 10, right: 10 },
  });

  doc.save(filename);
}

// ---------------------------------------------------------------------------
// Call Logs Report Export
// ---------------------------------------------------------------------------

// CSV/PDF columns (label + key). call_start_time is UTC, so it's localized into
// `call_start_local` by the row transform below before export.
const CALL_LOGS_COLUMNS = [
  { label: 'Call Start', key: 'call_start_local' },
  { label: 'Duration', key: 'call_length' },
  { label: 'Call Type', key: 'call_type_name' },
  { label: 'Event', key: 'event_id' },
  { label: 'Caller', key: 'caller' },
  { label: 'Receiver', key: 'receiver' },
];

// Maps a table column key (visibleColumns) → its export column key(s).
const CALL_LOGS_TABLE_TO_EXPORT = {
  call_start_time: ['call_start_local'],
  call_length: ['call_length'],
  call_type_name: ['call_type_name'],
  event_id: ['event_id'],
  caller: ['caller'],
  receiver: ['receiver'],
};

function filterCallLogsColumns(visibleColumns) {
  if (!visibleColumns || visibleColumns.length === 0) return CALL_LOGS_COLUMNS;
  const allowed = new Set();
  visibleColumns.forEach((k) => (CALL_LOGS_TABLE_TO_EXPORT[k] || []).forEach((c) => allowed.add(c)));
  return CALL_LOGS_COLUMNS.filter((c) => allowed.has(c.key));
}

/** Add a campus-local call start to each row for export (call_start_time is UTC). */
function callLogsExportRows(rows, tzOffset) {
  return (rows || []).map((r) => ({
    ...r,
    call_start_local: convertIsoUTCToLocal(r.call_start_time, tzOffset) || '',
  }));
}

/**
 * Export call logs to CSV.
 *
 * @param {Array<object>} rows — call records
 * @param {string} [filename='call-logs.csv']
 * @param {Array<string>} [visibleColumns] — visible table column keys (filters export columns)
 * @param {number} [tzOffset] — campus tz_offset for localizing call start
 */
export function exportCallLogsCsv(rows, filename = 'call-logs.csv', visibleColumns, tzOffset) {
  const headers = filterCallLogsColumns(visibleColumns);
  const csv = buildCsv(headers, callLogsExportRows(rows, tzOffset));
  downloadCsv(csv, filename);
}

/**
 * Export call logs to PDF (landscape A4).
 *
 * @param {Array<object>} rows — call records
 * @param {string} [title='Call Logs Report']
 * @param {string} [dateRange=''] — e.g. "01/15/2026 12:00 AM – 01/16/2026 11:59 PM"
 * @param {string} [filename='call-logs.pdf']
 * @param {Array<string>} [visibleColumns]
 * @param {number} [tzOffset]
 */
export function exportCallLogsPdf(rows, title = 'Call Logs Report', dateRange = '', filename = 'call-logs.pdf', visibleColumns, tzOffset) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const cols = filterCallLogsColumns(visibleColumns);
  const exportRows = callLogsExportRows(rows, tzOffset);
  const pdfHeaders = cols.map((c) => c.label);
  const pdfRows = exportRows.map((row) => cols.map((c) => (row[c.key] != null ? String(row[c.key]) : '')));

  doc.setFontSize(14);
  doc.text(title, 14, 15);
  if (dateRange) {
    doc.setFontSize(9);
    doc.text(dateRange, 14, 22);
  }

  autoTable(doc, {
    head: [pdfHeaders],
    body: pdfRows,
    startY: dateRange ? 28 : 22,
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [0, 129, 199], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    margin: { left: 10, right: 10 },
  });

  doc.save(filename);
}

// ---------------------------------------------------------------------------
// Hourly Summary Report Export
// ---------------------------------------------------------------------------

/** CSV column headers for hourly summary export */
const HOURLY_SUMMARY_HEADERS = [
  { label: 'Hour of Day', key: 'display_hour' },
  { label: '# Events', key: 'count' },
  { label: 'Avg Response (min)', key: 'avg_response_time' },
  { label: 'Min Response (min)', key: 'min_response_time' },
  { label: 'Max Response (min)', key: 'max_response_time' },
  { label: 'Avg Service Time (min)', key: 'avg_service_time' },
];

/**
 * Export hourly summary breakdown to CSV.
 *
 * @param {Array<object>} rows — 24 hourly aggregate rows
 * @param {string} [filename='hourly-summary.csv']
 */
export function exportHourlySummaryCsv(rows, filename = 'hourly-summary.csv') {
  const csv = buildCsv(HOURLY_SUMMARY_HEADERS, rows);
  downloadCsv(csv, filename);
}

/**
 * Export hourly summary breakdown to PDF (landscape A4).
 *
 * @param {Array<object>} rows — 24 hourly aggregate rows
 * @param {string} title — report title
 * @param {string} dateRange — e.g. "Jan 15, 2026 12:00 AM - Jan 16, 2026 11:59 PM"
 * @param {string} [filename='hourly-summary.pdf']
 */
export function exportHourlySummaryPdf(rows, title = 'Hourly Summary Report', dateRange = '', filename = 'hourly-summary.pdf') {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  const pdfHeaders = [
    'Hour of Day', '# Events', 'Avg Response (min)',
    'Min Response (min)', 'Max Response (min)', 'Avg Service Time (min)',
  ];

  const formatService = (val) => {
    if (val == null || val === 0 || val === '0' || val === '0.0') return '-';
    return String(val);
  };

  const pdfRows = rows.map((row) => [
    row.display_hour || '',
    row.count != null ? String(row.count) : '0',
    row.avg_response_time != null ? String(row.avg_response_time) : '',
    row.min_response_time != null ? String(row.min_response_time) : '',
    row.max_response_time != null ? String(row.max_response_time) : '',
    formatService(row.avg_service_time),
  ]);

  // Title
  doc.setFontSize(14);
  doc.text(title, 14, 15);
  if (dateRange) {
    doc.setFontSize(9);
    doc.text(dateRange, 14, 22);
  }

  autoTable(doc, {
    head: [pdfHeaders],
    body: pdfRows,
    startY: dateRange ? 28 : 22,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [0, 129, 199], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    margin: { left: 14, right: 14 },
  });

  doc.save(filename);
}

// ---------------------------------------------------------------------------
// Maintenance Report — CSV + PDF (on-premise and cloud)
// ---------------------------------------------------------------------------

const MAINTENANCE_ONPREMISE_CSV_HEADERS = [
  { label: 'Event Start', key: 'EventDateTime' },
  { label: 'Device', key: 'DeviceUse' },
  { label: 'Serial #', key: 'SerialNumber' },
  { label: 'Alert Type', key: 'AlertSubType' },
  { label: 'Resident Name', key: '_Resident_Names' },
  { label: 'Area', key: 'LocationArea' },
  { label: 'Location', key: 'LocationName' },
];

const MAINTENANCE_CLOUD_CSV_HEADERS = [
  { label: 'Detected (UTC)', key: 'event_datetime_utc' },
  { label: 'Condition', key: 'condition' },
  { label: 'Device Name', key: 'device_name' },
  { label: 'Device Serial #', key: 'manufacture_uuid' },
  { label: 'Area', key: 'area_name' },
  { label: 'Resident', key: 'resident_name' },
];

/**
 * Map table column keys → CSV header keys for Maintenance.
 * Some table columns are virtual/combined (e.g. _device_serial → DeviceUse + SerialNumber).
 */
const MAINTENANCE_ONPREMISE_TABLE_TO_CSV_MAP = {
  EventDateTime: ['EventDateTime'],
  _duration: [],
  AlertSubType: ['AlertSubType'],
  _residents: ['_Resident_Names'],
  _device_serial: ['DeviceUse', 'SerialNumber'],
  _location: ['LocationArea', 'LocationName'],
};

const MAINTENANCE_CLOUD_TABLE_TO_CSV_MAP = {
  event_datetime_utc: ['event_datetime_utc'],
  _duration: [],
  condition: ['condition'],
  _device_serial: ['device_name', 'manufacture_uuid'],
  area_name: ['area_name'],
  resident_name: ['resident_name'],
};

/** Filter maintenance headers by visible table columns */
function filterMaintenanceHeaders(headers, visibleColumns, tableMap) {
  if (!visibleColumns || visibleColumns.length === 0) return headers;
  const allowedKeys = new Set();
  visibleColumns.forEach((tableKey) => {
    const csvKeys = tableMap[tableKey];
    if (csvKeys) csvKeys.forEach((k) => allowedKeys.add(k));
  });
  return headers.filter((h) => allowedKeys.has(h.key));
}

/**
 * Flatten the Residents JSON string into comma-separated names.
 * V1 shape: `[{"fName":"John","lName":"Doe","id":5}]`
 */
function flattenResidents(row) {
  try {
    const arr = typeof row.Residents === 'string' ? JSON.parse(row.Residents) : row.Residents;
    if (Array.isArray(arr)) {
      return arr.map((r) => `${r.fName || ''} ${r.lName || ''}`.trim()).join(', ');
    }
    return '';
  } catch {
    return '';
  }
}

/**
 * Export maintenance report data to CSV.
 *
 * @param {Array} rows — report records
 * @param {boolean} isCloud — cloud vs on-premise column set
 * @param {string} filename
 * @param {Array<string>} [visibleColumns] — visible table column keys (filters CSV columns)
 */
export function exportMaintenanceCsv(rows, isCloud, filename = 'maintenance-report.csv', visibleColumns) {
  const allHeaders = isCloud ? MAINTENANCE_CLOUD_CSV_HEADERS : MAINTENANCE_ONPREMISE_CSV_HEADERS;
  const tableMap = isCloud ? MAINTENANCE_CLOUD_TABLE_TO_CSV_MAP : MAINTENANCE_ONPREMISE_TABLE_TO_CSV_MAP;
  const headers = filterMaintenanceHeaders(allHeaders, visibleColumns, tableMap);

  // For on-premise, flatten the Residents JSON into a temporary key
  const mappedRows = isCloud
    ? rows
    : rows.map((row) => ({ ...row, _Resident_Names: flattenResidents(row) }));

  const csv = buildCsv(headers, mappedRows);
  downloadCsv(csv, filename);
}

/**
 * Export maintenance report data to PDF.
 *
 * @param {Array} rows — report records
 * @param {boolean} isCloud — cloud vs on-premise column set
 * @param {string} title — report title
 * @param {string} dateRange — date range display string
 * @param {string} filename
 * @param {Array<string>} [visibleColumns] — visible table column keys (filters PDF columns)
 */
export function exportMaintenancePdf(rows, isCloud, title, dateRange, filename = 'maintenance-report.pdf', visibleColumns) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const allPdfCols = isCloud
    ? [
        { label: 'Detected (UTC)', key: 'event_datetime_utc' },
        { label: 'Condition', key: 'condition' },
        { label: 'Device Name', key: 'device_name' },
        { label: 'Device Serial #', key: 'manufacture_uuid' },
        { label: 'Area', key: 'area_name' },
        { label: 'Resident', key: 'resident_name' },
      ]
    : [
        { label: 'Event Start', key: 'EventDateTime' },
        { label: 'Device', key: 'DeviceUse' },
        { label: 'Serial #', key: 'SerialNumber' },
        { label: 'Alert Type', key: 'AlertSubType' },
        { label: 'Resident Name', key: '_Resident_Names' },
        { label: 'Area', key: 'LocationArea' },
        { label: 'Location', key: 'LocationName' },
      ];

  const tableMap = isCloud ? MAINTENANCE_CLOUD_TABLE_TO_CSV_MAP : MAINTENANCE_ONPREMISE_TABLE_TO_CSV_MAP;
  const filteredCols = filterMaintenanceHeaders(allPdfCols, visibleColumns, tableMap);
  const pdfHeaders = filteredCols.map((col) => col.label);

  // Prepare row data, flattening residents for on-premise
  const mappedRows = isCloud
    ? rows
    : rows.map((row) => ({ ...row, _Resident_Names: flattenResidents(row) }));

  const pdfRows = mappedRows.map((row) =>
    filteredCols.map((col) => row[col.key] != null ? String(row[col.key]) : '')
  );

  // Title
  doc.setFontSize(14);
  doc.text(title, 14, 15);
  if (dateRange) {
    doc.setFontSize(9);
    doc.text(dateRange, 14, 22);
  }

  autoTable(doc, {
    head: [pdfHeaders],
    body: pdfRows,
    startY: dateRange ? 28 : 22,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [0, 129, 199], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    margin: { left: 14, right: 14 },
  });

  doc.save(filename);
}

// ---------------------------------------------------------------------------
// Custom Event Text Templates Export
// ---------------------------------------------------------------------------

/** Column definitions for template CSV export */
const TEMPLATE_COLUMN_ORDER = ['output_type_name', 'text_format', 'field_count'];

const TEMPLATE_HEADERS_MAP = {
  output_type_name: 'Output Type',
  text_format: 'Template Format',
  field_count: 'Fields',
};

/**
 * Map visible column keys to their CSV-friendly equivalents.
 * The table uses 'text_format_display' but the actual data key is 'text_format'.
 */
const TEMPLATE_KEY_REMAP = {
  text_format_display: 'text_format',
};

/**
 * Export custom event text templates to CSV with only visible columns.
 *
 * @param {Array<object>} templates — enriched template data
 * @param {string} [filename='custom-event-text-templates.csv'] — download filename
 * @param {Array<string>} [visibleColumns] — visible column keys (exports all if omitted)
 */
export function exportTemplatesCsv(templates, filename = 'custom-event-text-templates.csv', visibleColumns) {
  // Remap visible column keys to data keys
  const mappedVisible = visibleColumns
    ? visibleColumns.map((k) => TEMPLATE_KEY_REMAP[k] || k)
    : null;

  const headers = TEMPLATE_COLUMN_ORDER
    .filter((colKey) => !mappedVisible || mappedVisible.includes(colKey))
    .map((colKey) => ({ label: TEMPLATE_HEADERS_MAP[colKey], key: colKey }));

  const csv = buildCsv(headers, templates);
  downloadCsv(csv, filename);
}
